import React, { useState } from 'react';
import { useFormik, FormikProvider, Form } from 'formik';
import * as XLSX from 'xlsx';
import { useSnackbar } from 'notistack';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Grid, Card, Stack, Button, Typography, Box, Alert } from '@material-ui/core';
import { LoadingButton } from '@material-ui/lab';
import axios from 'axios';
import { REST_API_END_POINT } from 'src/constants/Defaultvalues';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { Download, KeyboardBackspaceOutlined } from '@material-ui/icons';
import { UploadFile } from '@material-ui/icons';
import { UploadSingleFile } from 'src/components/upload';

export default function UploadProjects() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [fileName, setFileName] = useState(null)
  const [isUploading, setIsUploading] = useState(false);
  let user = JSON.parse(localStorage.getItem('user'))
  let userId = user.user_id

  const formik = useFormik({
    initialValues: {
      cover: null,
    },
    onSubmit: async (values) => {
      if (!values.cover) {
        enqueueSnackbar("Please select an Excel file to upload.", { variant: 'error' });
        return;
      }

      setIsUploading(true);
      const reader = new FileReader();
      reader.onload = async (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        let jsonData = XLSX.utils.sheet_to_json(sheet);

        // Trim keys and values
        jsonData = jsonData.map(obj => {
          const cleanedObj = {};
          for (const key in obj) {
            cleanedObj[key.trim()] = typeof obj[key] === 'string' ? obj[key].trim() : obj[key];
          }
          return cleanedObj;
        });

        try {
          const BATCH_SIZE = 50; // Process 50 rows at a time
          let totalBatches = Math.ceil(jsonData.length / BATCH_SIZE);
          let allFailedProjects = [];

          for (let i = 0; i < totalBatches; i++) {
            let batchData = jsonData.slice(i * BATCH_SIZE, (i + 1) * BATCH_SIZE);

            const response = await axios.post(REST_API_END_POINT + `community/mass-upload`, { data: batchData, userId });
            console.log(`Batch ${i + 1} of ${totalBatches} processed:`, response.data);

            if (response.data.status === 1) {
              enqueueSnackbar(`Batch ${i + 1} processed: ${response.data.message}`, { variant: 'success' });
              if (response.data.failedProjects && response.data.failedProjects.length > 0) {
                allFailedProjects = [...allFailedProjects, ...response.data.failedProjects];
              }
            } else {
              enqueueSnackbar(`Batch ${i + 1} failed: ${response.data.message}`, { variant: 'error' });
              if (response.data.failedProjects && response.data.failedProjects.length > 0) {
                allFailedProjects = [...allFailedProjects, ...response.data.failedProjects];
              }
            }
          }

          if (allFailedProjects.length > 0) {
            enqueueSnackbar("Some projects failed to upload. Downloading a list of failed projects.", { variant: 'warning' });
            const blob = new Blob([JSON.stringify(allFailedProjects, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'failedProjects.json';
            link.click();
            URL.revokeObjectURL(url);
          } else {
            enqueueSnackbar("All projects uploaded successfully!", { variant: 'success' });
          }
          navigate(PATH_DASHBOARD.community.root); // Navigate back to community list
        } catch (error) {
          console.error('Error uploading projects:', error);
          enqueueSnackbar("Error uploading projects. Please try again.", { variant: 'error' });
        } finally {
          setIsUploading(false);
          setFileName(null);
          formik.resetForm();
        }
      };
      reader.readAsArrayBuffer(values.cover);
    },
  });

  const handleDrop = (acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      formik.setFieldValue('cover', acceptedFiles[0]);
      setFileName(acceptedFiles[0].name);
    }
  };

  const handleGoBack = () => {
    window.history.back();
  };

  return (
    <FormikProvider value={formik}>
      <Form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
        <Grid container spacing={3} justifyContent="center" alignItems="center">
          <Grid item xs={12} md={12}>
            <Card sx={{ paddingLeft: "20px", paddingRight: "20px", paddingTop: "10px", margin: 'auto', height: 'fit-content' }}>
              <Button
                variant="outlined"
                onClick={handleGoBack}
                startIcon={
                  <Stack>
                    <KeyboardBackspaceOutlined />
                  </Stack>
                }
              >
                Back
              </Button>
              <Stack spacing={3}>
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <a href="/static/sample_projects.xlsx" download style={{ textDecoration: 'none' }}>
                    <Button
                      variant="outlined"
                      startIcon={<Download />}
                      type="button"
                    >
                      Download Sample Project Template
                    </Button>
                  </a>
                </Box>
                <Alert severity="info" sx={{ width: '100%' }}>
                  <Typography variant="body1"> Use the Appropriate Headers: `name`, `description`, `phone`, `email`, `poNumber`, `address`, `city`, `state`, `zip`, `client`, `salesRepNames`.
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1 }} color={'error.main'}> `name`, `client`, `salesRepNames`, `address`, `city`, `state`, `zip` are mandatory fields.
                  </Typography>
                  <Typography variant="body1" color={'warning.main'} sx={{ mt: 1 }}> (Please ensure `client` and `salesRepNames` already exist in the system. Project Name and PO Number must be unique.)</Typography>
                </Alert>

                <UploadSingleFile
                  maxSize={1024 * 1024 * 1024}
                  accept=".xlsx,.xls"
                  value={formik.values.cover}
                  onDrop={handleDrop}
                  error={Boolean(formik.touched.cover && formik.errors.cover)}
                />
                <Typography>{fileName ? fileName : ""}</Typography>
                {fileName && (
                  <LoadingButton sx={{ width: '100%', mb: 2, boxShadow: 'none' }} startIcon={<UploadFile />} type="submit" variant="contained" loading={isUploading}>
                    Upload and Process Projects
                  </LoadingButton>
                )}
              </Stack>
            </Card>
          </Grid>
        </Grid>
      </Form>
    </FormikProvider>
  );
}